import { motion } from "framer-motion";
import saintsData from "~/data/about-us/saints.json";
import { useFontStyle } from "~/hook/useFontStyle";
import useIntersectionObserver from "~/hook/useIntersectionObserver";
import { cn } from "~/lib/utils";

// Hero Section
const HeroSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="relative w-full mb-5"
    >
      <div className="w-full flex justify-center items-center px-4 md:px-6">
        <div className="w-full max-w-6xl text-center">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={isVisible ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h1 className={cn("text-3xl md:text-5xl font-bold text-textColor mb-4", medium)}>The Nand Saints</h1>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-primaryColor to-transparent mx-auto mb-6" />
            <p className={cn("text-lg md:text-xl text-textSecondaryColor italic", medium)}>
              3000 Ascetic Saints • Foundations of Faith • Eternal Legacy
            </p>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

// Introduction Section
const IntroductionSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, x: -30 }}
      animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          whileHover={{ scale: 1.01, boxShadow: "0 10px 30px rgba(217, 126, 87, 0.15)" }}
          className="bg-gradient-to-br from-secondaryColor/40 via-white to-secondaryColor/20 p-8 md:p-12 rounded-2xl border-l-4 border-primaryColor shadow-lg"
        >
          <p className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed text-justify", normalInter)}>
            {saintsData.content.introduction}
          </p>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Image Showcase Section
const ImageSection = () => {
  const [ref, isVisible] = useIntersectionObserver();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-primaryColor/30 hover:border-primaryColor/60 transition-all duration-500 group"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent z-10" />
          <motion.img
            whileHover={{ scale: 1.08 }}
            transition={{ duration: 0.6 }}
            src={saintsData.content.mainImage.src}
            alt={saintsData.content.mainImage.alt}
            className="w-full aspect-video object-cover"
            loading="lazy"
          />
          <div className="absolute bottom-0 left-0 right-0 z-20 p-6 md:p-10">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={isVisible ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <p className={cn("text-white text-xl md:text-3xl font-bold drop-shadow-2xl", "font-medium")}>The Assembly of Saints</p>
              <p className="text-white/90 text-sm md:text-base mt-2 drop-shadow-lg">Gathering in devotion to Swaminarayan Bhagwan</p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Vows Introduction
const VowsIntroSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.7 }}
      className="mb-12 md:mb-16 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto text-center">
        <motion.h2
          initial={{ opacity: 0, y: -10 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -10 }}
          transition={{ delay: 0.2 }}
          className={cn("text-2xl md:text-4xl font-bold text-textColor mb-6 inline-block border-b-4 border-primaryColor pb-3", medium)}
        >
          The Five Sacred Vows
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 0.4 }}
          className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed max-w-4xl mx-auto mt-6", normalInter)}
        >
          {saintsData.content.vowsIntroduction}
        </motion.p>
      </div>
    </motion.section>
  );
};

// Panch Vartman Cards Section
const PanchVartmanSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, rotateX: -15 },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
      variants={containerVariants}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {saintsData.content.panchVartman.map((vow, index) => (
            <motion.div
              key={vow.key}
              variants={cardVariants}
              whileHover={{
                y: -12,
                boxShadow: "0 25px 50px rgba(217, 126, 87, 0.3)",
                scale: 1.03,
              }}
              className="bg-gradient-to-br from-white via-secondaryColor/30 to-primaryColor/10 p-6 md:p-8 rounded-xl border-2 border-primaryColor/40 shadow-xl hover:border-primaryColor transition-all duration-300 group relative overflow-hidden"
            >
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-primaryColor/5 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500" />

              {/* Number Badge */}
              <motion.div
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
                className="relative w-14 h-14 bg-gradient-to-br from-primaryColor to-primaryColor/80 rounded-full flex items-center justify-center mb-5 shadow-lg z-10"
              >
                <span className={cn("text-white text-2xl font-bold", medium)}>{index + 1}</span>
              </motion.div>

              {/* Title with Sanskrit term */}
              <h3
                className={cn(
                  "text-xl md:text-2xl font-bold text-primaryColor mb-4 group-hover:text-textColor transition-colors duration-300 relative z-10",
                  medium
                )}
              >
                {vow.key}
              </h3>

              {/* Description */}
              <p className={cn("text-textSecondaryColor text-sm md:text-base leading-relaxed relative z-10", normalInter)}>{vow.value}</p>

              {/* Bottom accent line */}
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primaryColor via-primaryColor/50 to-transparent"
                style={{ transformOrigin: "left" }}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

// Vows Conclusion Section
const VowsConclusionSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.7 }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          whileHover={{ scale: 1.01 }}
          className="bg-gradient-to-r from-primaryColor/15 via-secondaryColor/40 to-primaryColor/15 p-8 md:p-10 rounded-2xl border-2 border-primaryColor/50 shadow-xl text-center"
        >
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={isVisible ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
            transition={{ delay: 0.3, duration: 0.6, type: "spring" }}
            className="w-16 h-16 bg-primaryColor rounded-full flex items-center justify-center mb-6 mx-auto shadow-lg"
          >
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </motion.div>
          <p className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed", normalInter)}>{saintsData.content.vowsConclusion}</p>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Accomplishments Section
const AccomplishmentsSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, x: -40 }}
      animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
      transition={{ duration: 0.8 }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ delay: 0.2 }}
          className={cn("text-2xl md:text-4xl font-bold text-textColor mb-8 inline-block border-b-4 border-primaryColor pb-3", medium)}
        >
          Their Monumental Accomplishments
        </motion.h2>

        <motion.div whileHover={{ x: 5, boxShadow: "0 15px 40px rgba(217, 126, 87, 0.2)" }} className="relative">
          {/* Decorative side line */}
          <div className="absolute left-0 top-0 bottom-0 w-2 bg-gradient-to-b from-primaryColor via-primaryColor/60 to-primaryColor/20 rounded-full" />

          <div className="pl-8 md:pl-12 bg-gradient-to-r from-secondaryColor/30 to-transparent p-8 md:p-10 rounded-r-2xl border border-primaryColor/20 hover:border-primaryColor/50 transition-all duration-300">
            <p className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed text-justify", normalInter)}>
              {saintsData.content.accomplishments}
            </p>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Modern Legacy Section
const ModernLegacySection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.8 }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          whileHover={{ scale: 1.02, boxShadow: "0 30px 60px rgba(217, 126, 87, 0.25)" }}
          className="bg-gradient-to-br from-primaryColor/20 via-secondaryColor/40 to-primaryColor/20 rounded-3xl p-10 md:p-14 border-4 border-primaryColor/40 shadow-2xl text-center relative overflow-hidden"
        >
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-br from-primaryColor/5 to-transparent" />

          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={isVisible ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="relative z-10"
          >
            <div className="w-20 h-20 bg-gradient-to-br from-primaryColor to-primaryColor/70 rounded-full flex items-center justify-center mb-6 mx-auto shadow-xl">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </div>

            <h2 className={cn("text-2xl md:text-3xl font-bold text-textColor mb-6", medium)}>Today&apos;s Legacy</h2>

            <p className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed", normalInter)}>{saintsData.content.modernLegacy}</p>

            {/* Decorative dots */}
            <div className="flex justify-center gap-3 mt-8">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0 }}
                  animate={isVisible ? { scale: 1 } : { scale: 0 }}
                  transition={{ delay: 0.6 + i * 0.1, duration: 0.3 }}
                  className="w-3 h-3 bg-primaryColor rounded-full shadow-md"
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

function Saints() {
  return (
    <div className="w-full bg-gradient-to-b from-white via-secondaryColor/5 to-white">
      {/* py-8 md:py-16 */}
      <div className="w-full ">
        <HeroSection />
        <IntroductionSection />
        <ImageSection />
        <VowsIntroSection />
        <PanchVartmanSection />
        <VowsConclusionSection />
        <AccomplishmentsSection />
        <ModernLegacySection />
      </div>
    </div>
  );
}

export default Saints;
